import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import ScrollReveal from '@/components/base/ScrollReveal';
import JsonLd from '@/components/base/JsonLd';
import { useTranslation } from 'react-i18next';

const serviceMeta: Record<string, { icon: string; image: string }> = {
  audit: { icon: 'ri-search-line', image: 'https://readdy.ai/api/search-image?query=Black%20African%20engineer%20with%20dark%20skin%20performing%20technical%20audit%20with%20tablet%20and%20measurement%20equipment%20at%20infrastructure%20site%20in%20West%20Africa%2C%20professional%20dark-skinned%20technician%20inspecting%20systems%2C%20bright%20daylight%2C%20technical%20assessment%20work%20photography&width=800&height=500&seq=serv-audit-01&orientation=landscape&nocache=true' },
  expertise: { icon: 'ri-user-star-line', image: 'https://readdy.ai/api/search-image?query=Black%20African%20technical%20expert%20with%20dark%20skin%20inspecting%20infrastructure%20with%20professional%20certification%20equipment%20in%20Africa%2C%20certified%20dark-skinned%20engineer%20checking%20system%20performance%2C%20industrial%20site%20with%20safety%20gear%2C%20professional%20technical%20assessment%20photography&width=800&height=500&seq=serv-expertise-01&orientation=landscape&nocache=true' },
  informatique: { icon: 'ri-computer-line', image: 'https://readdy.ai/api/search-image?query=Modern%20industrial%20server%20room%20with%20green%20energy%20monitoring%20screens%20and%20LED%20indicators%2C%20high-tech%20data%20center%20with%20renewable%20energy%20management%20system%2C%20professional%20IT%20infrastructure%20for%20energy%20company%2C%20clean%20modern%20technology%20photography&width=800&height=500&seq=serv-it-01&orientation=landscape' },
  environnement: { icon: 'ri-leaf-line', image: 'https://readdy.ai/api/search-image?query=Black%20African%20environmental%20engineer%20with%20dark%20skin%20conducting%20sustainability%20study%20at%20infrastructure%20site%20with%20green%20landscape%20in%20Africa%2C%20professional%20dark-skinned%20environmentalist%20assessing%20project%20impact%2C%20clean%20natural%20scenery%2C%20environmental%20compliance%20work%20photography&width=800&height=500&seq=serv-env-01&orientation=landscape&nocache=true' },
  btp: { icon: 'ri-building-2-line', image: 'https://readdy.ai/api/search-image?query=Black%20African%20BTP%20construction%20workers%20with%20dark%20skin%20welding%20and%20assembling%20steel%20metal%20structure%20for%20industrial%20infrastructure%20in%20Africa%2C%20professional%20civil%20engineering%20and%20metal%20construction%20with%20dark-skinned%20workers%20wearing%20safety%20helmets%20and%20vests%2C%20construction%20site%20with%20scaffolding%20and%20concrete%20work%2C%20bright%20daylight&width=800&height=500&seq=serv-btp-01&orientation=landscape&nocache=true' },
  telecom: { icon: 'ri-base-station-line', image: 'https://storage.helloreaddy.io/project_files/dd4db55e-adff-421a-ab07-f57f9542cdee/fd12f5e6-44a4-43fa-9549-f4b629ca57c9_compressed_ChatGPT-Image-5-aot-2026-09_46_40.webp' },
  transport: { icon: 'ri-truck-line', image: 'https://storage.helloreaddy.io/project_files/dd4db55e-adff-421a-ab07-f57f9542cdee/264d0edc-d384-4b1b-aec4-cf672c8bc633_compressed_ChatGPT-Image-3-aot-2026-15_21_13.webp' },
};

const subServiceIcons: Record<string, string> = {
  location: 'ri-tools-line',
};

export default function AutresServices() {
  const { t } = useTranslation();
  const services = t('autresServices.services', { returnObjects: true }) as Array<Record<string, unknown>>;
  const whyItems = t('autresServices.whyUs.items', { returnObjects: true }) as Array<Record<string, string>>;
  const whyIcons = ['ri-award-line', 'ri-team-line', 'ri-shield-check-line'];

  return (
    <Layout>
      <JsonLd data={{
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Autres Services Techniques IS TELECOM',
        description: 'Audit et conseils, expertise technique, informatique et réseaux, services environnementaux, construction métallique et BTP, télécommunication, transport et logistique.',
        url: 'https://is-telecom.co/autres-services',
        itemListElement: services.map((s, i) => ({
          '@type': 'Service', position: i + 1, name: String(s.title || ''), description: String(s.fullDesc || '').slice(0, 150), provider: { '@type': 'Organization', name: 'IS TELECOM' },
        })),
      }} />
      {/* Hero */}
      <section className="relative py-20 bg-background-950 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src="https://readdy.ai/api/search-image?query=Abstract%20technical%20blueprint%20pattern%20with%20engineering%20lines%20and%20geometric%20shapes%20on%20dark%20background%2C%20dark%20navy%20background%20for%20technical%20services%20company%2C%20modern%20corporate%20design%20with%20subtle%20green%20teal%20accents&width=1920&height=700&seq=serv-hero-01&orientation=landscape" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-16">
          <h1 className="text-4xl md:text-6xl font-bold font-heading text-white mb-4">{t('autresServices.heroTitle')}</h1>
          <p className="text-lg md:text-xl text-background-300 max-w-3xl mx-auto">{t('autresServices.heroSubtitle')}</p>
        </div>
      </section>

      {/* Intro */}
      <ScrollReveal variant="fade-up">
        <section className="py-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-lg text-foreground-600 leading-relaxed">{t('autresServices.intro')}</p>
          </div>
        </section>
      </ScrollReveal>

      {/* Services Detail */}
      <section className="py-24 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {services.map((service) => {
              const meta = serviceMeta[String(service.id || '')];
              if (!meta) return null;
              return (
                <ScrollReveal key={String(service.id)} variant="fade-up" threshold={0.08}>
                  <div className="max-w-3xl">
                    <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center mb-5">
                      <i className={`${meta.icon} text-primary-600 text-2xl w-7 h-7 flex items-center justify-center`}></i>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-4">{String(service.title || '')}</h2>
                    <p className="text-foreground-500 leading-relaxed mb-6">{String(service.fullDesc || '')}</p>
                    <h3 className="font-bold text-foreground-950 mb-3 text-sm">{t('autresServices.prestationsLabel')}</h3>
                    <ul className="space-y-3 mb-8">
                      {(service.details as string[] || []).map((d: string, i: number) => (
                        <li key={i} className="flex items-start gap-3 text-foreground-500">
                          <i className="ri-check-line text-primary-500 mt-0.5 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                          <span>{d}</span>
                        </li>
                      ))}
                    </ul>

                    {(service.subServices as Array<Record<string, unknown>> || []).length > 0 && (
                      <div className="mb-8">
                        <h3 className="font-bold text-foreground-950 mb-4 text-sm">{t('autresServices.subServicesLabel')}</h3>
                        <div className="space-y-4">
                          {(service.subServices as Array<Record<string, unknown>>).map((sub: Record<string, unknown>) => {
                            const subIcon = subServiceIcons[String(sub.id || '')] || 'ri-stack-line';
                            return (
                              <div key={String(sub.id)} className="bg-background-50 rounded-2xl p-5 border border-background-200/70">
                                <div className="flex items-start gap-3 mb-3">
                                  <div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <i className={`${subIcon} text-accent-600 text-lg w-5 h-5 flex items-center justify-center`}></i>
                                  </div>
                                  <div>
                                    <h4 className="font-bold text-foreground-950 text-base">{String(sub.title || '')}</h4>
                                  </div>
                                </div>
                                <p className="text-foreground-500 text-sm leading-relaxed mb-3">{String(sub.desc || '')}</p>
                                <ul className="space-y-1.5">
                                  {(sub.details as string[] || []).map((d: string, i: number) => (
                                    <li key={i} className="flex items-start gap-2 text-foreground-400 text-sm">
                                      <i className="ri-check-line text-accent-500 mt-0.5 w-4 h-4 flex items-center justify-center flex-shrink-0"></i>
                                      <span>{d}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <Link to="/devis" className="inline-flex items-center gap-2 text-accent-500 hover:text-accent-600 font-semibold cursor-pointer">
                      {t('autresServices.ctaLink')}
                      <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center"></i>
                    </Link>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="py-24 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal variant="fade-up">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold font-heading text-foreground-950 mb-4">{t('autresServices.whyUs.title')}</h2>
            </div>
          </ScrollReveal>
          <div className="grid md:grid-cols-3 gap-6">
            {whyItems.map((item, idx) => (
              <ScrollReveal key={item.title} variant="fade-up" delay={idx * 100} threshold={0.1}>
                <div className="bg-white rounded-3xl p-8 text-center border border-background-200/70 hover:shadow-md transition-shadow">
                  <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-5">
                    <i className={`${whyIcons[idx]} text-primary-600 text-2xl w-7 h-7 flex items-center justify-center`}></i>
                  </div>
                  <h3 className="font-bold font-heading text-foreground-950 text-lg mb-3">{item.title}</h3>
                  <p className="text-foreground-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <ScrollReveal variant="fade-up" threshold={0.1}>
        <section className="py-24 bg-background-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-5xl font-bold font-heading text-white mb-4">{t('autresServices.cta.title')}</h2>
            <p className="text-lg text-background-300 max-w-3xl mx-auto mb-8">{t('autresServices.cta.subtitle')}</p>
            <Link to="/devis" className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-950 px-8 py-4 rounded-full text-lg font-semibold transition-all cursor-pointer whitespace-nowrap">
              {t('autresServices.cta.button')}
              <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center"></i>
            </Link>
          </div>
        </section>
      </ScrollReveal>
    </Layout>
  );
}